import { TaskType } from '@/shared/domain/taskTypes';
import {
    normalizeLabel,
    foldPlural,
} from '@/features/datasets/utils/labelFuzzyMatching';

/**
 * Column best matching one of `needles` for auto-selection. Needles are
 * tried in preference order; for each, a whole-name match under the same
 * fuzzy normalization used for label auto-mapping (case / separators /
 * accents / plural folding — so "Labels", "label", "EXAMPLES" all match)
 * beats substring containment of the normalized needle ("example_text",
 * "Summary Col").
 */
export const findColumnFuzzy = (columns, needles) => {
    const keyed = (columns || []).map((column) => {
        const key = normalizeLabel(column);
        return { column, key, folded: foldPlural(key) };
    });
    for (const needle of needles) {
        const needleKey = foldPlural(normalizeLabel(needle));
        const exact = keyed.find(
            (entry) => entry.key === needleKey || entry.folded === needleKey
        );
        if (exact) return exact.column;
        const partial = keyed.find((entry) => entry.key.includes(needleKey));
        if (partial) return partial.column;
    }
    return undefined;
};

/**
 * Default label / output column for auto-selection, per task type.
 * Classification prefers "labels"; summarization "summary"; content
 * generation "output", then "response", then "summary" — all fuzzy.
 */
export const defaultLabelColumn = (taskType, columns) => {
    if (taskType === TaskType.NER) {
        return findColumnFuzzy(columns, ['entities', 'labels']);
    }
    if (taskType === TaskType.classification) {
        return findColumnFuzzy(columns, ['labels']);
    }
    if (taskType === TaskType.summarization) {
        return findColumnFuzzy(columns, ['summary']);
    }
    if (taskType === TaskType.contentGeneration) {
        return findColumnFuzzy(columns, ['output', 'response', 'summary']);
    }
    return undefined;
};
